import React, { useState, useRef, useEffect } from 'react';
import { getInsuranceTypeName } from '../utils/insuranceTypeUtils';
import { DOCUMENT_TYPE } from '../utils/insuranceTypeUtils';

const InsuranceCompanySelect = ({ selectedInsuranceCompanies, setSelectedInsuranceCompanies, insuranceCompanies, insuranceType, documentType, isHistory = false }) => {
  const [showInsurancePopup, setShowInsurancePopup] = useState(false);
  const insurancePopupRef = useRef(null);
  const targetCompanies = insuranceCompanies.filter(company => company.insuranceType === insuranceType);

  const typeName = getInsuranceTypeName(insuranceType);
  const allTypeCompanyIds = targetCompanies.map((company) => company.indexName);
  const isAllSelected =
    targetCompanies.length > 0 &&
    targetCompanies.every((company) =>
      selectedInsuranceCompanies.some(
        (selected) => selected.indexName === company.indexName
      )
    );
  const selectAllCheckboxId = `insurance-company-select-all-${insuranceType}`;

  // Show/hide insurance company popup
  const toggleInsurancePopup = () => {
    setShowInsurancePopup(!showInsurancePopup);
  };

  // Handle insurance company selection
  const handleInsuranceCompanySelect = (company) => {
    setSelectedInsuranceCompanies(prev => {
      const isSelected = prev.some(selected => selected.indexName === company.indexName);

      if (isSelected) {
        return prev.filter(selected => selected.indexName !== company.indexName);
      } else {
        return [...prev, {
          name: company.name,
          nameKana: company.nameKana,
          indexName: company.indexName,
          insuranceType: company.insuranceType
        }];
      }
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedInsuranceCompanies(
      isAllSelected 
        ? [] 
        : targetCompanies.map((company) => ({
            name: company.name,
            nameKana: company.nameKana,
            indexName: company.indexName,
            insuranceType: company.insuranceType
          }))
    );
  };

  // Get text to display selected insurance companies
  const getSelectedInsuranceCompaniesText = () => {
    if (selectedInsuranceCompanies.length === 0) {
      if (documentType === DOCUMENT_TYPE.HIKIUKE) {
        return "全保険会社";
      } else {
        return "指定なし";
      }
    } else if (selectedInsuranceCompanies.length <= 5) {
      // If 5 or fewer, display all (separated by line breaks)
      return (
        <div className="selected-companies-list">
          {selectedInsuranceCompanies.map((company, index) => (
            <div key={index} className="selected-company-item">
              {company.name}
            </div>
          ))}
        </div>
      );
    } else {
      // If more than 5, display first 5 and show count of remaining
      const displayCompanies = selectedInsuranceCompanies.slice(0, 5);
      const remainingCount = selectedInsuranceCompanies.length - 5;

      return (
        <div className="selected-companies-list">
          {displayCompanies.map((company, index) => (
            <div key={index} className="selected-company-item">
              {company.name}
            </div>
          ))}
          <div className="selected-company-item remaining-count">
            他{remainingCount}件
         </div>
        </div>
      );
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (insurancePopupRef.current && !insurancePopupRef.current.contains(event.target) &&
          !event.target.closest('.option-value[data-type="insurance"]')) {
        setShowInsurancePopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="selection-option insurance-company-selection">
      <div
        className="option-label"
        onClick={toggleInsurancePopup}
        style={{ cursor: 'pointer' }}
      >
        {typeName}会社
        <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 3V14H11" stroke="#0073BC" strokeWidth="2"/>
          <rect x="5" y="1" width="8" height="9" fill="white" stroke="#0073BC" strokeWidth="2"/>
        </svg>
        {documentType === 'hikiuke' || isHistory ? null : <span className="required-mark">*</span>}
      </div>
      <div
        className="option-value"
        data-type="insurance"
        onClick={toggleInsurancePopup}
      >
        {getSelectedInsuranceCompaniesText()}
      </div>

      {/* Insurance company popup (larger size, shown on right) */}
      {showInsurancePopup && (
        <div className={`popup-base insurance-company-popup ${showInsurancePopup ? 'show' : ''}`} ref={insurancePopupRef}>
          <div className="popup-header">
            <span>{typeName}会社選択</span>
            <div className="popup-header-actions">
            <button 
              type="button"
              className="popup-complete-btn" 
              onClick={toggleInsurancePopup}
              aria-label={`${typeName}会社の選択を完了してポップアップを閉じる`}
            >
              選択完了
            </button>
            </div>
          </div>
          <div className="popup-content insurance-checkbox-grid-3">
            {targetCompanies.length === 0 ? (
              <div className="no-companies-message">
                <p>選択可能な{typeName}会社がありません。</p>
              </div>
            ) : (
              <>
              <div className="company-checkbox-item select-all-inline">
                <label className="checkbox-native-box">
                  <input
                    className="checkbox-native"
                    type="checkbox"
                    id={selectAllCheckboxId}
                    checked={isAllSelected}
                    onChange={handleToggleSelectAll}
                  />
                </label>
                <label htmlFor={selectAllCheckboxId}>全て選択</label>
              </div>
                {targetCompanies.map((company) => {
                  const checkboxId = `insurance-company-${insuranceType}-${company.indexName}`;
                  return (
                    <div key={company.indexName} className="company-checkbox-item">
                        <label className="checkbox-native-box">
                          <input
                            className="checkbox-native"
                            type="checkbox"
                            id={checkboxId}
                            checked={selectedInsuranceCompanies.some(
                              (selected) => selected.indexName === company.indexName
                            )}
                            onChange={() => handleInsuranceCompanySelect(company)}
                          />
                        </label>
                        <label htmlFor={checkboxId}>{company.name}</label>
                      </div>
                    );
                  })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceCompanySelect;
